import { Injectable } from '@angular/core';
import { ApiService } from 'src/app/core/services/api.service';
import { Permission, User } from 'src/app/models/Users';
import { Observable, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectAuthUserData } from 'src/app/core/store/selectors/authentication.selectors';
import { selectContextEntity } from 'src/app/core/store/selectors/context.selectors';
import { Entity } from 'src/app/models/entities.model';
// tslint:disable: variable-name

export type PermissionType = 'operation' | 'group';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  private _context: Entity;
  private _user: User;
  private roles: Permission;
  private internal_role: Permission;

  private loaded = false;
  public loading = true;

  private user$ = this.store.select(selectAuthUserData);
  private context$ = this.store.select(selectContextEntity);

  constructor(
    private api: ApiService,
    private store: Store<any>
  ) {

    combineLatest([this.user$, this.context$]).subscribe(([user, context]) => {
      this._user = user;
      this._context = context;
      this.roles = null;
      this.internal_role = null;
      if (!user || !context) {
        this.loaded = false;
        this.loading = true;
      } else {
        this.roles = this._user.roles.find(r => r.target_id == context.id);
        this.internal_role = this._user.internal_role;
        this.loaded = true;
        this.loading = false;
      }
    });

  }

  get user() {
    return new User(Object.assign({}, this._user));
  }


  get context() {
    return new Entity(Object.assign({}, this._context));
  }

  getContext() {
    return this._context;
  }

  targetPemissions(targetId): Observable<Permission[]> {
    return this.api.get<Permission[]>(`permissions/target/${targetId}/list`).pipe(map(res => res.payload));
  }

  getUserContextRoles(): Observable<Permission> {

    const context$ = this.store.select(selectContextEntity);
    const user$ = this.store.select(selectAuthUserData);

    return combineLatest([user$, context$]).pipe(
      take(1),
      map(([user, context]) => {
        const CONTEXT_ROLE = user.roles.find(r => r.target_id == context.id);
        CONTEXT_ROLE.target = context;
        CONTEXT_ROLE.user = user;
        return CONTEXT_ROLE;
      }));

  }

  checkPermission(permission: string, role: Permission): boolean {
    if (role.permissions) {
      const perm = role.permissions;
      if (perm.owner) { return true; }
      if (perm.operations?.admin) { return true; }
      if (perm[permission]) { return perm[permission] === true; }
    }
    return false;
  }

  checkOperation(operation: string, role: Permission): boolean {
    if (!!role.permissions) {
      const perm = role.permissions;
      if (perm.owner) { return true; }

      if (!!perm.operations) {
        const operations = perm.operations;
        if (operations[operation]) { return operations[operation] === true; }
      }
      return false;

    }
    return false;
  }

  savePermission(permission: Permission) {
    return this.api.post(`permissions`, permission).pipe(map(res => res.payload));
  }

  hasPermission(permission: string, targetType: 'internal' | 'entity' = 'entity') {
    if (!this.loaded) { return false; }
    if (this.roles?.permissions.owner) { return true; }
    if (this.roles?.permissions?.operations?.admin) { return true; }
    if (this.user.user_type === 'A') { return true; }
    if (this.context.source_name === 'athletes' && Entity.realAge(this.context.birth) < 18) {
      if (this.roles.permissions.legal_responsible) {
        return true;
      }
    }

    const operations = targetType == 'internal'
      ? this.internal_role.permissions.operations
      : this.roles.permissions.operations;
    if (operations && operations[permission]) { return true; }

    return false;
  }

  get isIntancePermission() {
    return !this.roles.id;
  }

  isLegalResponsible() {
    if (!this.loaded) { return false; }
    if (this.context.source_name === 'athletes' && Entity.realAge(this.context.birth) < 18) {
      if (this.roles.permissions.legal_responsible) {
        return true;
      }
    }
    return false;
  }

  hasGroupPermission(permission: string) {
    if (!this.loaded) { return false; }
    if (this.roles.permissions.owner) { return true; }
    const groups = this.roles.permissions.groups;
    if (groups && groups[permission]) { return true; }
    return false;
  }

  hasOneOfPermission(permissions: string | string[], targetType: 'internal' | 'entity' = 'entity') {
    if (!this.loaded) { return false; }
    if (this.roles.permissions.owner) { return true; }
    if (this.roles.permissions?.operations?.admin) { return true; }
    if (permissions.includes('gioco-master-admin')) {
      return this.user.user_type == 'A';
    }

    let result = false;
    const _permissions = Array.isArray(permissions) ? permissions : [permissions];
    const operations = targetType == 'entity' ? this.roles.permissions.operations : this.internal_role.permissions.operations;
    _permissions.forEach(p => {
      if (operations && operations[p] === true) {
        result = true;
      }
    });
    return result;
  }

  hasAllPermission(permissions: string | string[], targetType: 'internal' | 'entity' = 'entity') {
    if (!this.loaded) { return false; }
    if (this.roles.permissions.owner) { return true; }
    if (this.roles.permissions?.operations?.admin) { return true; }

    const operations = targetType == 'entity' ? this.roles.permissions.operations : this.internal_role.permissions.operations;
    const _permissions = Array.isArray(permissions) ? permissions : [permissions];
    let permitted = 0;
    _permissions.forEach(p => {
      if (operations && operations[p] === true) {
        permitted++;
      }
    });

    return permissions.length === permitted;
  }

  hasInternalPermission(permission: string | string[]) {
    if (!this.loaded) { return false; }
    if (this.user.user_type === 'A') { return true; }
    const permissions = Array.isArray(permission) ? permission : [permission];
    const operations = this.internal_role.permissions.operations;

    let result = false;
    permissions.forEach(_p => {
      if (operations[_p] === true) {
        result = true;
      }
    });

    return result;
  }

  get isOwner() {
    if (!this.loaded) { return false; }
    return !!this.roles.permissions.owner;
  }

  setPermissions(o: { [index: string]: boolean }) {
    const keys = Object.keys(o);
    keys.forEach(k => {
      if (k == 'internal') {
        o[k] = ['I', 'A'].includes(this.user.user_type);
      }
      o[k] = this.hasPermission(k);
    });
  }

  setInternalPermissions(o: { [index: string]: boolean }) {
    const keys = Object.keys(o);
    keys.forEach(k => {
      o[k] = this.hasInternalPermission(k);
    });
  }

  clearPermissions(o: { [index: string]: boolean }) {
    const keys = Object.keys(o);
    keys.forEach(k => {
      o[k] = false;
    });
  }

}
